import { observer } from "mobx-react";
import React, { useContext, useEffect, useState } from "react";
import { Megalith, MegalithContext } from "./stores/Megalith";
import {
  createTheme,
  MantineProvider,
  Container,
  TextInput,
  CopyButton,
  Button,
  Paper,
  Textarea,
  Divider,
  Stack,
  Image,
  Box,
  Group,
  SimpleGrid,
  Anchor,
} from "@mantine/core";
import { Title, Text } from "@mantine/core";
import { format } from "d3-format";
import { formatPriceForMillions } from "./helpers/numbers";
import { MEGALITH_NODE_ADDRESS } from "./helpers/constants";

import {
  IconBrandTelegram,
  IconCheck,
  IconCopy,
  IconQrcode,
  IconPlugConnected,
} from "@tabler/icons-react";
import QRCode from "react-qr-code";

const theme = createTheme({
  /** Put your mantine theme override here */
});

const MegalithContainer: React.FC = observer(() => {
  const megalith = useContext<Megalith>(MegalithContext);
  const [showQRCode, setShowQRCode] = useState(false);
  const heroImage = document.getElementById("root")?.dataset.heroImage;

  useEffect(() => {
    console.log("starting megalith container");
    megalith.hydrateDataFromPage();
  }, []);

  const makeBitcoinAmount = () => {
    const btcSize =
      megalith.megalithDataFrozen?.data.megalith_node_info.size_btc;
    return format(".1f")(btcSize);
  };

  const makeDollarAmount = () => {
    const btcSize =
      megalith.megalithDataFrozen?.data.megalith_node_info.size_btc;
    console.log("btc size", btcSize);
    const btcRate = megalith.megalithDataFrozen?.data.btc_usd_price;
    console.log("btc rate", btcRate);
    return formatPriceForMillions(btcSize * btcRate);
  };

  return (
    <>
      {megalith.hasLoaded && (
        <>
          <Stack>
            <Paper withBorder radius="md" p={0} style={{ overflow: "hidden" }}>
              <Box pos="relative">
                <Image
                  pos="absolute"
                  inset={0}
                  h="100%"
                  w="100%"
                  fit="cover"
                  src={heroImage}
                  alt="Lightning striking the Megalith"
                />
                {/* mobile: text runs full width, so darken top-to-bottom */}
                <Box
                  pos="absolute"
                  inset={0}
                  hiddenFrom="sm"
                  style={{
                    background:
                      "linear-gradient(180deg, rgba(10,9,20,0.55) 0%, rgba(10,9,20,0.86) 45%, rgba(10,9,20,0.95) 100%)",
                  }}
                />
                {/* desktop: text sits in the left third, so darken left-to-right */}
                <Box
                  pos="absolute"
                  inset={0}
                  visibleFrom="sm"
                  style={{
                    background:
                      "linear-gradient(90deg, rgba(10,9,20,0.93) 0%, rgba(10,9,20,0.78) 45%, rgba(10,9,20,0.05) 100%)",
                  }}
                />
                <Stack
                  pos="relative"
                  gap={8}
                  justify="flex-end"
                  mih={{ base: 240, sm: 320 }}
                  p={{ base: "lg", sm: "xl" }}
                >
                  <Title order={1} fz={{ base: 24, sm: 38 }} c="white">
                    The Megalith Node
                  </Title>
                  <Text fz={{ base: "sm", sm: "md" }} c="gray.3" maw={540}>
                    One of the biggest and most stable routing nodes on the
                    Lightning Network — powered by the relentless ☀️ sun of the
                    American Southwest.
                  </Text>
                  <Group gap="sm" mt="xs">
                    <Button
                      leftSection={<IconPlugConnected size={18} />}
                      component="a"
                      href="/get_a_channel"
                      target="_blank"
                    >
                      Get A Channel
                    </Button>
                    <Button
                      variant="default"
                      component="a"
                      href="https://docs.megalithic.me"
                      target="_blank"
                    >
                      Read the docs
                    </Button>
                  </Group>
                </Stack>
              </Box>

              <SimpleGrid
                cols={{ base: 2, sm: 4 }}
                spacing="lg"
                verticalSpacing="md"
                p={{ base: "lg", sm: "md" }}
              >
                {[
                  { label: "Capacity", value: makeDollarAmount() },
                  { label: "Bitcoin", value: makeBitcoinAmount() + " BTC" },
                  {
                    label: "Open channels",
                    value:
                      megalith.megalithDataFrozen?.data.megalith_node_info
                        .channels,
                  },
                  { label: "Uptime", value: "99.9999%" },
                ].map((stat) => (
                  <Box key={stat.label}>
                    <Text fz={{ base: 20, sm: 26 }} fw={700} lh={1.1}>
                      {stat.value}
                    </Text>
                    <Text size="xs" c="dimmed" tt="uppercase" lts={0.5}>
                      {stat.label}
                    </Text>
                  </Box>
                ))}
              </SimpleGrid>

              <Divider />

              <SimpleGrid
                cols={{ base: 1, sm: 2 }}
                spacing="lg"
                verticalSpacing={8}
                p={{ base: "lg", sm: "md" }}
              >
                {[
                  "Running in a secure datacenter",
                  "Trunk fiber connection",
                  "Fast global pings",
                  "Industry-leading LSP (Lightning Service Provider)",
                ].map((feature) => (
                  <Group key={feature} gap={8} wrap="nowrap" align="flex-start">
                    <IconCheck
                      size={16}
                      color="var(--mantine-color-teal-4)"
                      style={{ marginTop: 3, flexShrink: 0 }}
                    />
                    <Text size="sm">{feature}</Text>
                  </Group>
                ))}
                <Group gap={8} wrap="nowrap" align="flex-start">
                  <IconCheck
                    size={16}
                    color="var(--mantine-color-teal-4)"
                    style={{ marginTop: 3, flexShrink: 0 }}
                  />
                  <Text size="sm">
                    Reasonably entertaining{" "}
                    <Anchor href="https://docs.megalithic.me">
                      documentation
                    </Anchor>
                  </Text>
                </Group>
              </SimpleGrid>
            </Paper>
            <Paper>
              {/* <Title order={2}>🌴 How can the Megalith Node help you?</Title> */}
              {/* 
              <Text size="md">
                We recommend that you
                <Text fw={700} span>
                  {" "}
                  open a channel{" "}
                </Text>
                to Megalith.
              </Text> */}
            </Paper>
            <Paper withBorder p="md" mb="md">
              <Title order={4}>
                ✅ Get an inbound channel from Megalith LSP...
              </Title>
              <Text size="md">
                Megalith LSP provides luxury inbound Lightning channels,
                designed for connoisseurs.{" "}
              </Text>
              <Button
                leftSection={<IconPlugConnected />}
                component="a"
                href={"/get_a_channel"}
                target="_blank"
              >
                Get A Channel
              </Button>
            </Paper>
            <Paper withBorder p="md" mb="md">
              <Title order={4}>✨ Want to try running your own node?</Title>
              <Anchor href="https://rizful.com">
                <Image
                  h={100}
                  w="auto"
                  fit="contain"
                  radius="md"
                  src="https://daxwktpz4hkwd.cloudfront.net/logos/rizful-logo-mobile.png"
                  style={{ verticalAlign: "middle", marginRight: "10px" }}
                />
              </Anchor>
              <Text size="md">
                Our project{" "}
                <Anchor href="https://rizful.com" target="_blank">
                  Rizful.com
                </Anchor>{" "}
                is a super-fast and easy way to get started with Lightning. Use
                the free service to send and receive, or run your own node in
                the cloud with{" "}
                <Anchor
                  target="_blank"
                  href="https://rizful.com/node_in_the_cloud"
                >
                  Rizful Node
                </Anchor>
                .
              </Text>
            </Paper>
            {/* 
            <Paper withBorder p="md" mb="md">
              <Title order={4}>✨ Get on NOSTR today</Title>
              <Anchor href="https://rizful.com/get_on_nostr_today">
                <Image
                  h={200}
                  w="auto"
                  fit="contain"
                  radius="md"
                  src="https://daxwktpz4hkwd.cloudfront.net/onboarding/nostr_app_running_on_your_phone.webp"
                  style={{ verticalAlign: "middle", marginRight: "10px" }}
                />
              </Anchor>
              <Text mt="md" size="md">
                Nostr is decentralized, uncensorable, free, and a lot of fun.
                It's the future's social network, available today.{" "}
                <Anchor href="https://rizful.com/get_on_nostr_today">
                  Follow this guide to get on Nostr
                </Anchor>
                .
              </Text>
            </Paper> */}

            <Paper withBorder p="md" mb="md">
              <Title order={4}>✅ Open a channel to The Megalith Node...</Title>

              <Textarea
                radius="md"
                mt="8"
                mb="8"
                maw="60%"
                readOnly
                label="Megalith Node Address"
                value={MEGALITH_NODE_ADDRESS}
                onFocus={(e) => e.currentTarget.select()}
              />
              <CopyButton value={MEGALITH_NODE_ADDRESS}>
                {({ copied, copy }) => (
                  <Button
                    leftSection={<IconCopy />}
                    color={copied ? "teal" : "blue"}
                    onClick={copy}
                  >
                    {copied ? "Copied!" : "Copy address"}
                  </Button>
                )}
              </CopyButton>
              <Button
                leftSection={<IconQrcode />}
                ml="sm"
                onClick={() => setShowQRCode(!showQRCode)}
              >
                {showQRCode ? "Hide QR Code" : "QR Code"}
              </Button>
              {showQRCode && (
                <Paper withBorder m="md" p="md">
                  <QRCode value={MEGALITH_NODE_ADDRESS} />
                </Paper>
              )}
              <Text mt="md" size="sm" c="dimmed">
                - minimum channel size: 1,000,000 satoshis
              </Text>
              <Text size="sm" c="dimmed">
                - need a smaller channel?{" "}
                <Anchor
                  target="_blank"
                  href="https://docs.megalithic.me/lightning-tips/dealing-with-minimum-channel-sizes"
                >
                  Open to our "small channel" node
                </Anchor>
              </Text>
              <Text size="sm" c="dimmed">
                - opening a channel is free
              </Text>
              <Text size="sm" c="dimmed">
                - <Anchor href="/contact">contact us </Anchor>
                for help
              </Text>
            </Paper>

            <Title order={4}>💡 Ready to learn more about Lightning?</Title>
            <Text size="md">
              For background info, and technical help, check out our{" "}
              <Anchor href="https://docs.megalithic.me">
                opinionated guide
              </Anchor>
              , designed for new entrants to Lightning. Browse the{" "}
              <Anchor href="/power_list">power list</Anchor> to learn about the
              biggest players on the network.
              {/*  If you're interested in Taproot
              Assets, we're maintaining a beta{" "}
              <Anchor href="/taproot_assets">buy & sell exchange</Anchor> and{" "}
              <Anchor href="/taproot_assets_explorer">
                taproot assets explorer
              </Anchor>
              . */}
            </Text>
            <Title order={4}>
              ⚙️ Looking for a Lightning Service Provider (LSP)?
            </Title>
            <Text size="md">
              If you're building a wallet or other application, and you need an
              automated API service to open channels for your users, check out
              our{" "}
              <Anchor href="https://docs.megalithic.me/lightning-services/lsp1-get-inbound-liquidity-for-mobile-clients">
                highly-available http-based LSPS1 service
              </Anchor>
              .
            </Text>
            {/* <Image
              mb="md"
              h={150}
              w="auto"
              fit="contain"
              radius="md"
              src="https://d3u5cu1kr5hxm.cloudfront.net/images/wide-landscape-megalith-branding-crop.webp"
              style={{ verticalAlign: "middle", marginRight: "10px" }}
            /> */}
          </Stack>
        </>
      )}
    </>
  );
});

export default MegalithContainer;
